// O que fazer depois de uma autenticacao bem-sucedida, e como confinar o
// usuario `finance_only` (#333).
//
// O backend manda `user.access_scope` ("full" | "finance_only") no login 200,
// no sucesso do /2fa/verify/ e no /verify-token/. Um usuario finance_only so
// existe para chegar a https://financeiro.dale365.com/, que autentica pelo
// cookie `dale365_token`; o token dele e recusado (403) em todo endpoint da
// plataforma. Campo ausente = "full", para funcionar contra o backend atual.
//
// Isto e conveniencia de navegacao: quem impede o acesso e o backend.
//
// Modulo puro (sem window/document) para ser testado com node --test.

import { resolveSsoReturn, ssoCookieString, trustedSsoTarget } from './ssoReturn.mjs';

export const FINANCE_URL = 'https://financeiro.dale365.com/';
export const FINANCE_ONLY = 'finance_only';
export const FULL = 'full';
// Onde o Vuex guarda o objeto do usuario (store/modules/auth.js, SET_USER).
export const USER_DATA_KEY = 'userData';

const FINANCE_HOSTNAME = new URL(FINANCE_URL).hostname;

// Botao Financeiro no menu (#391): o backend manda `user.finance_access` para
// quem e socio no Financeiro (FINANCE_PARTNER_USERNAMES) sem mudar o resto do
// acesso. So `true` exato liga; o Financeiro decide o que a pessoa ve la.
export function hasFinanceAccess(user) {
  return Boolean(user) && user.finance_access === true;
}

export function accessScopeOf(user) {
  return user && user.access_scope === FINANCE_ONLY ? FINANCE_ONLY : FULL;
}

// Escopo do usuario ja gravado no navegador. Dado ausente ou corrompido = full.
export function storedAccessScope(storage) {
  try {
    return accessScopeOf(JSON.parse(storage.getItem(USER_DATA_KEY) || 'null'));
  } catch (e) {
    return FULL;
  }
}

// `next` de um subdominio dale365 que pode atravessar o passo do 2FA. Devolve a
// URL normalizada ou null; nunca devolve o valor cru.
export function trustedNextForTwoFactor(rawNext, baseOrigin) {
  const target = trustedSsoTarget(rawNext, baseOrigin);
  return target ? target.toString() : null;
}

// Depois de um login/2FA/vinculo bem-sucedido. Devolve:
//   cookie      - string para `document.cookie` (sempre que houver token)
//   scope       - "full" | "finance_only"
//   externalUrl - navegacao de pagina inteira obrigatoria, ou null quando o
//                 call site segue o destino que ja tinha (continuacao do
//                 Desktop, /partidas-v2, ...).
// finance_only vai SEMPRE para o Financeiro, e o `next` e ignorado. Para os
// demais, `rawNext` so vira destino se passar por trustedSsoTarget.
export function resolvePostLogin({ payload, location, rawNext = null }) {
  const token = payload && payload.token;
  const scope = accessScopeOf(payload && payload.user);
  const cookie = token ? ssoCookieString(token, location.hostname, location.protocol) : null;
  const externalUrl =
    scope === FINANCE_ONLY ? FINANCE_URL : trustedNextForTwoFactor(rawNext, location.origin);
  return { cookie, scope, externalUrl };
}

export function isFinanceOnlyAllowedPath(path) {
  return path === '/login' || path === '/blocked' || String(path).startsWith('/2fa/');
}

// Guard do router para o usuario finance_only ja logado. Devolve:
//   { action: 'allow' }                  - deixa a rota de autenticacao abrir
//   { action: 'redirect', url, cookie }  - regrava o cookie e vai ao Financeiro
//   { action: 'login' }                  - anti-loop disparou: fica no /login
//
// O anti-loop e o do resolveSsoReturn (mesma chave por destino, janela de
// 30 s): se o Financeiro recusar o token e devolver o usuario, a segunda
// passagem para no /login em vez de quicar entre os dois sites - e nunca cai
// numa tela da plataforma.
export function resolveFinanceConfinement({ path, rawNext, token, location, storage, now = Date.now() }) {
  if (isFinanceOnlyAllowedPath(path)) {
    if (path !== '/login') return { action: 'allow' };
    // Ja logada e devolvida pelo Financeiro (/login?next=https://financeiro...).
    const target = trustedSsoTarget(rawNext, location.origin);
    if (!target || target.hostname !== FINANCE_HOSTNAME) return { action: 'allow' };
    const back = resolveSsoReturn({ rawNext: FINANCE_URL, token, location, storage, now });
    return back ? { action: 'redirect', url: back.url, cookie: back.cookie } : { action: 'allow' };
  }
  const sso = resolveSsoReturn({ rawNext: FINANCE_URL, token, location, storage, now });
  return sso ? { action: 'redirect', url: sso.url, cookie: sso.cookie } : { action: 'login' };
}
